import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import {
  Save, Loader2, Check, Lock, Trash2,
  User, HeartPulse, MapPin, Phone, Users,
} from 'lucide-react'
import { apiGet, patchJson, apiDelete } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import { useMe } from '../../lib/me.js'
import { canEditClinical } from '../../lib/role.js'
import AddressSelect from '../../components/AddressSelect.jsx'
import Combobox from '../../components/Combobox.jsx'
import {
  PREFIX_TH, PREFIX_EN, RELATIONSHIPS,
  ALLERGY_OPTIONS, BLEEDING_MED_OPTIONS, DISEASE_OPTIONS,
} from '../../data/medicalOptions.js'
import { OCCUPATIONS } from '../../data/occupations.js'
import {
  Banner, bkkTodayISO, Button, ConfirmDialog, ErrorState, Field, Input, Loading, Select, useToast,
} from '../../kit'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const SECTIONS = [
  { id: 'sec-personal',  label: 'ส่วนตัว',   icon: User },
  { id: 'sec-medical',   label: 'การแพทย์',  icon: HeartPulse },
  { id: 'sec-address',   label: 'ที่อยู่',    icon: MapPin },
  { id: 'sec-contact',   label: 'ติดต่อ',    icon: Phone },
  { id: 'sec-emergency', label: 'ฉุกเฉิน',    icon: Users },
]
const SECTION_IDS = SECTIONS.map((s) => s.id)

// Section nav highlight. Scroll-spy follows scrolling, BUT a click is
// authoritative: select(id) sets the active section + scrolls, then pauses the
// spy until the user scrolls again — so short sections near the page bottom
// (ที่อยู่ / ติดต่อ) that can't reach the detection line still highlight on click.
function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0])
  const lockRef = useRef(false)

  useEffect(() => {
    const scroller = document.querySelector('main')
    function compute() {
      if (lockRef.current) return
      if (scroller && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 4) {
        setActive(ids[ids.length - 1]); return
      }
      const ph = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ph')) || 88
      const lineY = (window.innerWidth < 1024 ? 56 : 0) + ph + 64
      let current = ids[0]
      for (const id of ids) {
        const el = document.getElementById(id)
        if (!el) continue
        if (el.getBoundingClientRect().top - lineY <= 0) current = id
        else break
      }
      setActive(current)
    }
    const unlock = () => { lockRef.current = false }
    compute()
    const target = scroller || window
    target.addEventListener('scroll', compute, { passive: true })
    // A real user gesture resumes the spy after a click-jump.
    window.addEventListener('wheel', unlock, { passive: true })
    window.addEventListener('touchmove', unlock, { passive: true })
    window.addEventListener('keydown', unlock)
    window.addEventListener('resize', compute)
    return () => {
      target.removeEventListener('scroll', compute)
      window.removeEventListener('wheel', unlock)
      window.removeEventListener('touchmove', unlock)
      window.removeEventListener('keydown', unlock)
      window.removeEventListener('resize', compute)
    }
  }, [ids])

  const select = useCallback((id) => {
    lockRef.current = true
    setActive(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return [active, select]
}

/* "-" is the legacy "none" marker for the *_other free-text fields. */
const cleanOther = (v) => (v == null || v === '-' ? '' : String(v))
const arr = (v) => (Array.isArray(v) ? v : [])

function makeDraft(p) {
  if (!p) return null
  return {
    prefix: p.prefix || '', name: p.name || '', surname: p.surname || '', nickname: p.nickname || '',
    prefix_eng: p.prefix_eng || '', name_eng: p.name_eng || '', surname_eng: p.surname_eng || '',
    birthday: p.birthday || '', occupation: p.occupation || '', idcard: p.idcard || '',
    allergy: arr(p.allergy), allergy_other: cleanOther(p.allergy_other),
    disease: arr(p.disease), disease_other: cleanOther(p.disease_other),
    bleeding_med: arr(p.bleeding_med),
    remark: p.remark || '',
    address: { line1: '', subdistrict: '', district: '', province: '', postcode: '', ...(p.address || {}) },
    contact: { phone: '', email: '', facebook: '', facebook_id: '', line: '', ...(p.contact || {}) },
    contact_person: { phone: '', name: '', relationship: '', ...(p.contact_person || {}) },
  }
}

function buildPayload(d) {
  const name = (d.name || '').trim()
  const surname = (d.surname || '').trim()
  return {
    prefix: d.prefix, name, surname,
    fullname: [name, surname].filter(Boolean).join(' '),
    nickname: d.nickname,
    prefix_eng: d.prefix_eng, name_eng: d.name_eng, surname_eng: d.surname_eng,
    birthday: d.birthday || null,
    occupation: d.occupation, idcard: d.idcard,
    allergy: d.allergy, allergy_other: d.allergy_other,
    disease: d.disease, disease_other: d.disease_other,
    bleeding_med: d.bleeding_med,
    remark: d.remark,
    address: d.address, contact: d.contact, contact_person: d.contact_person,
  }
}

function ageFrom(birthday) {
  if (!birthday) return null
  const d = new Date(birthday)
  if (Number.isNaN(d.getTime())) return null
  // Today in Bangkok (was pinned to a dev date, so it disagreed with the header's age).
  const now = new Date(bkkTodayISO() + 'T00:00:00')
  let a = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--
  return a >= 0 && a < 130 ? a : null
}

export default function PatientInfo() {
  const { patient, setPatient } = useOutletContext() || {}
  const me = useMe()
  const navigate = useNavigate()
  const locked = !canEditClinical(me)
  // Anyone who can edit patients (front desk / doctor / admin) may clean up an empty
  // duplicate they created. The guard (no appointment/treatment/billing) keeps it safe.
  const canDelete = canEditClinical(me)
  // phase: 'idle' → 'confirm' (ConfirmDialog open); blocks = categories preventing deletion (null when clear)
  const [del, setDel] = useState({ phase: 'idle', busy: false, blocks: null, err: '' })
  const toast = useToast()

  const [draft, setDraft] = useState(() => makeDraft(patient))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [branchName, setBranchName] = useState('')
  const [active, selectSection] = useActiveSection(SECTION_IDS)

  // Reset the draft only when switching patients — NOT on every patient-object
  // change. Status is edited out-of-band (StatusControl → special endpoint →
  // setPatient), and we don't want that to clobber in-progress form edits.
  useEffect(() => { setDraft(makeDraft(patient)) }, [patient?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const baseline = useMemo(() => JSON.stringify(buildPayload(makeDraft(patient) || {})), [patient])
  const dirty = !!draft && JSON.stringify(buildPayload(draft)) !== baseline

  // Live (2026-08-18). PatientLayout refetches the patient row whenever the
  // `patient_update - patient` trigger fires, so this form can go stale under an
  // edit made at another station.
  //
  // Clean form  → resync silently: the fields simply show the new truth.
  // Dirty form  → do NOT touch the draft. Whoever is typing keeps their text, and
  //               the save bar warns them that บันทึก will overwrite the other
  //               edit. This form PATCHes every field it holds, so a silent
  //               resync-under-typing would be the one way to lose someone's work.
  const seenPatient = useRef(patient)
  const [remoteEdited, setRemoteEdited] = useState(false)
  useEffect(() => {
    if (seenPatient.current === patient) return
    seenPatient.current = patient
    if (dirty) { setRemoteEdited(true); return }
    setDraft(makeDraft(patient))
    setRemoteEdited(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient])

  // Resolve branch_id → branch name (shown read-only).
  useEffect(() => {
    const bid = patient?.branch_id
    if (bid == null) { setBranchName(''); return }
    let alive = true
    apiGet('/api/branches')
      .then((res) => {
        if (!alive) return
        const list = Array.isArray(res) ? res : (res?.items || [])
        const b = list.find((x) => String(x.id ?? x.branch_id) === String(bid))
        setBranchName(b?.name || b?.branch_name || `#${bid}`)
      })
      .catch(() => { if (alive) setBranchName(`#${bid}`) })
    return () => { alive = false }
  }, [patient?.branch_id])

  if (!patient || !draft) return <Loading />

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }))
  const setNested = (group, k, v) => setDraft((d) => ({ ...d, [group]: { ...d[group], [k]: v } }))

  async function save() {
    setSaving(true); setErr(null)
    try {
      const payload = buildPayload(draft)
      await patchJson(`/api/patients/${patient.id}`, payload)
      setPatient?.({ ...patient, ...payload })
      try { const fresh = await apiGet(`/api/patients/${patient.id}`); if (fresh?.id) setPatient?.(fresh) } catch { /* keep optimistic */ }
      toast.success('บันทึกแล้ว')
    } catch (e) {
      setErr(e?.message || 'บันทึกไม่สำเร็จ')
      toast.error('บันทึกไม่สำเร็จ', { detail: e?.message })
    } finally {
      setSaving(false)
    }
  }

  // Check the patient is empty (no appointment/treatment/billing), then either arm the
  // confirm or report what's blocking. The server re-checks this authoritatively on delete.
  async function checkDeletable() {
    setDel({ phase: 'idle', busy: true, blocks: null, err: '' })
    try {
      const [appts, txs, bills] = await Promise.all([
        apiGet('/api/appointments', { patient_id: patient.id, type: 'patient' }).then(asList).catch(() => []),
        apiGet('/api/treatments', { patient_id: patient.id, type: 'patient', per_page: 1 }).then(asList).catch(() => []),
        apiGet('/api/billing', { patient_id: patient.id, per_page: 1 }).then(asList).catch(() => []),
      ])
      const blocks = []
      if (appts.filter((a) => a && a.active !== false).length > 0) blocks.push('นัดหมาย')
      if (txs.length > 0) blocks.push('การรักษา')
      if (bills.length > 0) blocks.push('รายการเรียกเก็บ')
      if (blocks.length) setDel({ phase: 'idle', busy: false, blocks, err: '' })
      else setDel({ phase: 'confirm', busy: false, blocks: null, err: '' })
    } catch (e) {
      setDel({ phase: 'idle', busy: false, blocks: null, err: e?.message || 'ตรวจสอบข้อมูลไม่สำเร็จ' })
    }
  }
  async function confirmDelete() {
    setDel((s) => ({ ...s, busy: true, err: '' }))
    try {
      await apiDelete(`/api/patients/${patient.id}`)
      toast.success('ลบผู้ป่วยแล้ว')
      navigate('/', { replace: true })
    } catch (e) {
      const msg = e?.message || 'ลบผู้ป่วยไม่สำเร็จ'
      setDel({ phase: 'idle', busy: false, blocks: null, err: msg })
      toast.error('ลบผู้ป่วยไม่สำเร็จ', { detail: e?.message })
    }
  }
  const patientLabel = [patient.name, patient.surname].filter(Boolean).join(' ') || `#${patient.id}`

  const age = ageFrom(draft.birthday)

  return (
    <div className="pb-24">
      {/* Sticky section nav — pinned just below the (sticky) patient top bar.
          Identity + allergy live in that top bar, so this stays slim. */}
      <div className="sticky top-[calc(3.5rem+var(--ph,5.5rem))] lg:top-[var(--ph,5.5rem)] z-10 -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 py-2 mb-4 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75 border-b border-slate-200/70">
        <div className="flex items-center gap-2">
          <nav className="flex-1 min-w-0">
            <div className="flex gap-1 overflow-x-auto -mx-1 px-1">
              {SECTIONS.map((s) => {
                const on = active === s.id
                const Icon = s.icon
                return (
                  <button key={s.id} type="button" onClick={() => selectSection(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[14px] font-medium whitespace-nowrap transition-colors flex-none ${
                      on ? 'bg-brand-50 text-brand-700' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}>
                    <Icon size={14} strokeWidth={2} className={on ? 'text-brand-600' : 'text-slate-400'} />
                    {s.label}
                  </button>
                )
              })}
            </div>
          </nav>
        </div>
      </div>

      {locked && (
        <Banner tone="warn" icon={Lock} className="mb-4">คุณไม่มีสิทธิ์แก้ไขข้อมูลผู้ป่วย (อ่านอย่างเดียว)</Banner>
      )}

      {err && <ErrorState title="บันทึกไม่สำเร็จ" message={err} className="mb-4" />}

      <fieldset disabled={locked} className={`min-w-0 ${locked ? 'opacity-70' : ''}`}>
        <div className="space-y-4">
            {/* Personal ----------------------------------------------------- */}
            <Section id="sec-personal" icon={User} title="ข้อมูลส่วนตัว" tint="brand">
            <div className="space-y-4">
              {/* Name — Thai */}
              <SubGroup label="ชื่อ–นามสกุล (ไทย)">
                <NameRow
                  prefix={<Sel label="คำนำหน้า" value={draft.prefix} onChange={(v) => set('prefix', v)} options={PREFIX_TH} />}
                  first={<Txt label="ชื่อ" value={draft.name} onChange={(v) => set('name', v)} />}
                  last={<Txt label="นามสกุล" value={draft.surname} onChange={(v) => set('surname', v)} />}
                />
              </SubGroup>
              {/* Name — English */}
              <SubGroup label="ชื่อ–นามสกุล (อังกฤษ)">
                <NameRow
                  prefix={<Sel label="Prefix" value={draft.prefix_eng} onChange={(v) => set('prefix_eng', v)} options={PREFIX_EN} />}
                  first={<Txt label="First name" value={draft.name_eng} onChange={(v) => set('name_eng', v)} />}
                  last={<Txt label="Last name" value={draft.surname_eng} onChange={(v) => set('surname_eng', v)} />}
                />
              </SubGroup>
              {/* Other personal details */}
              <Grid>
                <Txt label="ชื่อเล่น" value={draft.nickname} onChange={(v) => set('nickname', v)} />
                <Txt label="วันเกิด" type="date" value={draft.birthday} onChange={(v) => set('birthday', v)} hint={age != null ? `อายุ ${age} ปี` : null} />
                <Txt label="เลขบัตรประชาชน" value={draft.idcard} onChange={(v) => set('idcard', v)} digits maxLength={13} placeholder="เลข 13 หลัก" />
                {/* Combobox, not the kit SearchSelect: อาชีพ accepts free text that is not in the list. */}
                <Field label="อาชีพ">
                  <Combobox value={draft.occupation} onChange={(v) => set('occupation', v)} options={OCCUPATIONS} placeholder="เลือกหรือพิมพ์อาชีพ" freeText dense />
                </Field>
                <Txt label="HN" value={patient.HN || patient.hn || ''} locked />
                <Txt label="สาขา" value={branchName} locked />
              </Grid>
            </div>
          </Section>

          {/* Medical -------------------------------------------------------- */}
          <Section id="sec-medical" icon={HeartPulse} title="ประวัติการแพทย์" tint="rose">
            <div className="space-y-4">
              <Chips label="ประวัติแพ้ยา / อาหาร" options={ALLERGY_OPTIONS} value={draft.allergy} onChange={(v) => set('allergy', v)}
                other={draft.allergy_other} onOther={(v) => set('allergy_other', v)} otherLabel="แพ้อื่นๆ — ระบุ" />
              <Chips label="โรคประจำตัว" options={DISEASE_OPTIONS} value={draft.disease} onChange={(v) => set('disease', v)}
                other={draft.disease_other} onOther={(v) => set('disease_other', v)} otherLabel="โรคอื่นๆ — ระบุ" />
              <Chips label="ยาที่มีผลต่อการแข็งตัวของเลือด" options={BLEEDING_MED_OPTIONS} value={draft.bleeding_med} onChange={(v) => set('bleeding_med', v)} />
            </div>
          </Section>

          {/* Address -------------------------------------------------------- */}
          <Section id="sec-address" icon={MapPin} title="ที่อยู่" tint="sky">
            <AddressSelect value={draft.address} onChange={(addr) => setDraft((d) => ({ ...d, address: addr }))} disabled={locked} />
          </Section>

          {/* Contact -------------------------------------------------------- */}
          <Section id="sec-contact" icon={Phone} title="ช่องทางติดต่อ" tint="violet">
            <Grid>
              <Txt label="เบอร์โทร" value={draft.contact.phone} onChange={(v) => setNested('contact', 'phone', v)} type="tel" digits maxLength={10}
                invalid={!!draft.contact.phone && draft.contact.phone.length !== 10} errorHint="เบอร์โทรต้องมี 10 หลัก" placeholder="0XXXXXXXXX" />
              <Txt label="อีเมล" value={draft.contact.email} onChange={(v) => setNested('contact', 'email', v)} type="email"
                invalid={!!draft.contact.email && !EMAIL_RE.test(draft.contact.email)} errorHint="รูปแบบอีเมลไม่ถูกต้อง" placeholder="name@example.com" />
              {patient.line_display_name
                ? <Txt label="LINE" value={patient.line_display_name} locked hint="เชื่อมต่อผ่าน LINE OA แล้ว" />
                : <Txt label="LINE" value={draft.contact.line} onChange={(v) => setNested('contact', 'line', v)} />}
              <Txt label="Facebook" value={draft.contact.facebook} onChange={(v) => setNested('contact', 'facebook', v)} />
            </Grid>
          </Section>

          {/* Emergency contact ---------------------------------------------- */}
          <Section id="sec-emergency" icon={Users} title="ผู้ติดต่อกรณีฉุกเฉิน" tint="amber">
            <Grid>
              <Txt label="ชื่อผู้ติดต่อ" value={draft.contact_person.name} onChange={(v) => setNested('contact_person', 'name', v)} />
              <Sel label="ความสัมพันธ์" value={draft.contact_person.relationship} onChange={(v) => setNested('contact_person', 'relationship', v)} options={RELATIONSHIPS} />
              <Txt label="เบอร์โทร" value={draft.contact_person.phone} onChange={(v) => setNested('contact_person', 'phone', v)} type="tel" digits maxLength={10}
                invalid={!!draft.contact_person.phone && draft.contact_person.phone.length !== 10} errorHint="เบอร์โทรต้องมี 10 หลัก" placeholder="0XXXXXXXXX" />
            </Grid>
          </Section>

          {/* Remark --------------------------------------------------------- */}
        </div>
      </fieldset>

      {/* Danger zone — delete an empty patient (admin only) ------------------ */}
      {canDelete && (
        <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center flex-none"><Trash2 size={18} /></div>
            <div className="flex-1 min-w-0">
              <h3 className="text-[14px] font-semibold text-slate-800">ลบผู้ป่วย</h3>
              <p className="text-[12.5px] text-slate-500 mt-0.5">ลบได้เฉพาะผู้ป่วยที่ยังไม่มีนัดหมาย การรักษา และรายการเรียกเก็บ · การลบไม่สามารถย้อนกลับได้</p>
              {del.blocks && (
                <p className="mt-2 text-[12.5px] text-amber-700">ไม่สามารถลบได้ เนื่องจากผู้ป่วยรายนี้ยังมี: {del.blocks.join(' · ')}</p>
              )}
              {del.err && <p className="mt-2 text-[12.5px] text-rose-700">{del.err}</p>}
              <Button variant="danger-soft" size="sm" icon={Trash2} onClick={checkDeletable} loading={del.busy && del.phase !== 'confirm'} disabled={del.busy} className="mt-3">
                {del.busy && del.phase !== 'confirm' ? 'กำลังตรวจสอบ…' : 'ลบผู้ป่วยรายนี้'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {del.phase === 'confirm' && (
        <ConfirmDialog
          tone="danger"
          title="ลบผู้ป่วยถาวร?"
          message={`ผู้ป่วย “${patientLabel}”${patient.HN || patient.hn ? ` (HN ${patient.HN || patient.hn})` : ''} และข้อมูลส่วนตัว ประวัติการแพทย์ ที่อยู่ และช่องทางติดต่อทั้งหมดจะถูกลบ · ย้อนกลับไม่ได้`}
          confirmLabel="ลบถาวร"
          busy={del.busy}
          onConfirm={confirmDelete}
          onCancel={() => setDel({ phase: 'idle', busy: false, blocks: null, err: '' })}
        />
      )}

      {/* Floating save bar — only when there are unsaved changes */}
      {dirty && !locked && (
        <div className="fixed inset-x-0 bottom-5 z-30 px-4 pointer-events-none">
          <div className="mx-auto max-w-xl flex items-center gap-3 rounded-2xl bg-slate-900/95 backdrop-blur text-white shadow-2xl shadow-slate-900/30 ring-1 ring-white/10 px-4 py-3 pointer-events-auto animate-ss-pop-in">
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-none animate-pulse" />
            <span className="flex-1 text-[14px] font-medium">
              {remoteEdited
                ? 'มีคนแก้ข้อมูลคนไข้นี้จากเครื่องอื่น · บันทึกแล้วข้อมูลของคุณจะทับของเขา'
                : 'มีการเปลี่ยนแปลงที่ยังไม่ได้บันทึก'}
            </span>
            <button type="button" onClick={() => { setDraft(makeDraft(patient)); setRemoteEdited(false) }} disabled={saving}
              className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50">
              ยกเลิก
            </button>
            <button type="button" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[14px] font-semibold bg-white text-slate-900 hover:bg-slate-100 transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={2.25} />}
              {saving ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Presentational bits                                                        */
/* -------------------------------------------------------------------------- */

const TINTS = {
  brand:  'bg-brand-50 text-brand-600 ring-brand-100',
  rose:   'bg-rose-50 text-rose-500 ring-rose-100',
  sky:    'bg-sky-50 text-sky-600 ring-sky-100',
  violet: 'bg-violet-50 text-violet-600 ring-violet-100',
  amber:  'bg-amber-50 text-amber-600 ring-amber-100',
  slate:  'bg-slate-100 text-slate-500 ring-slate-200',
}

function Section({ id, icon: Icon, title, tint = 'slate', children }) {
  return (
    <section id={id} className="scroll-mt-[calc(3.5rem+var(--ph,5.5rem)+3.25rem)] lg:scroll-mt-[calc(var(--ph,5.5rem)+3.25rem)] bg-white border border-slate-200/80 rounded-2xl shadow-sm shadow-slate-200/40">
      <header className="px-5 pt-4 pb-3.5 flex items-center gap-2.5 border-b border-slate-100">
        <span className={`w-8 h-8 rounded-xl flex items-center justify-center ring-1 ${TINTS[tint] || TINTS.slate}`}>
          <Icon size={15} strokeWidth={2} />
        </span>
        <div className="text-[14px] font-semibold text-slate-700 tracking-tight">{title}</div>
      </header>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  )
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4">{children}</div>
}

function SubGroup({ label, children }) {
  return (
    <div>
      <div className="text-[12.5px] font-semibold text-slate-400 uppercase tracking-wide mb-2">{label}</div>
      {children}
    </div>
  )
}

function NameRow({ prefix, first, last }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_1fr] gap-3">
      {prefix}{first}{last}
    </div>
  )
}

function Txt({ label, value, onChange, locked, type = 'text', inputMode, maxLength, placeholder, hint, digits, invalid, errorHint }) {
  const handle = (raw) => {
    let val = raw
    if (digits) val = val.replace(/\D+/g, '')
    if (maxLength) val = val.slice(0, maxLength)
    onChange(val)
  }
  if (locked) {
    return (
      <Field label={label} hint={hint}>
        <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[13.5px] text-slate-500 flex items-center justify-between gap-2 select-none">
          <span className="truncate">{value || '—'}</span>
          <Lock size={13} className="flex-none text-slate-400" />
        </div>
      </Field>
    )
  }
  return (
    <Field label={label} hint={hint} error={invalid ? errorHint : null}>
      <Input
        type={type} value={value ?? ''} onChange={(e) => handle(e.target.value)}
        inputMode={digits ? 'numeric' : inputMode} maxLength={maxLength} placeholder={placeholder}
      />
    </Field>
  )
}

function Sel({ label, value, onChange, options }) {
  const opts = useMemo(() => [{ value: '', label: '—' }, ...options.map((o) => ({ value: o, label: o }))], [options])
  return (
    <Field label={label}>
      <Select value={value ?? ''} onChange={(v) => onChange(v ?? '')} options={opts} placeholder="—" />
    </Field>
  )
}

function Chips({ label, options, value, onChange, other, onOther, otherLabel }) {
  const sel = arr(value)
  const toggle = (o) => onChange(sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o])
  return (
    <Field label={label}>
      <div>
        <div className="flex flex-wrap gap-2">
          {options.map((o) => {
            const on = sel.includes(o)
            return (
              <button key={o} type="button" onClick={() => toggle(o)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[14px] font-medium ring-1 transition-all duration-150 ${
                  on ? 'bg-brand-600 text-white ring-brand-600 shadow-sm shadow-brand-600/20' : 'bg-white text-slate-600 ring-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}>
                {on && <Check size={12} strokeWidth={3} />}{o}
              </button>
            )
          })}
        </div>
        {onOther && (
          <Input value={other ?? ''} onChange={(e) => onOther(e.target.value)} placeholder={otherLabel || 'อื่นๆ — ระบุ'} className="mt-2.5" />
        )}
      </div>
    </Field>
  )
}
